import { Injectable } from '@angular/core';
import { PlanogramDrop } from '../models/layout/planogram-drop';
import { PlanogramLayout } from '../models/layout/planogram-layout';
import { PlanogramProduct } from '../models/layout/planogram-product';
import { PlanogramShelf } from '../models/layout/planogram-shelf';
import { ProductOrientation } from '../models/layout/product-orientation';
import { ProductReference } from '../models/layout/product-reference';
import { PlanogramService } from './planogram.service';
import { LayoutUtilitiesService } from '../utilities/layout.utilities';

export class ValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ValidationError';
  }
}

@Injectable({
  providedIn: 'root',
})
export class PlanogramValidationService {
  constructor(
    private planogramService: PlanogramService,
    private layoutUtils: LayoutUtilitiesService
  ) {}

  validateDropHeight(drop: PlanogramDrop, layout?: PlanogramLayout): void {
    const shelfLipHeight = this.planogramService.getShelfLipHeight();
    const minShelfSpacing = this.planogramService.getShelfMinimumSpacing();
    const defaultDropHeight = this.planogramService.getDropHeight();
    const defaultDropFooterHeight = this.planogramService.getDropFooterHeight();

    const totalHeight = this.layoutUtils.calculateDropTotalHeight(
      drop,
      drop.lipHeight ?? shelfLipHeight,
      drop.minSpacing ?? minShelfSpacing,
      layout
    );
    const availableHeight =
      (drop.height ?? defaultDropHeight) -
      (drop.footerHeight ?? defaultDropFooterHeight);

    if (totalHeight > availableHeight) {
      throw new ValidationError(
        `Drop height exceeded.\nMaximum available height is ${availableHeight.toFixed(
          1
        )}mm, ` + `used height would be ${totalHeight.toFixed(1)}mm `
      );
    }
  }

  validateShelfWidth(shelf: PlanogramShelf, shelfWidth: number): void {
    if (shelf.filledWidth > shelfWidth) {
      throw new ValidationError(
        `Shelf width exceeded.\nMaximum width is ${shelfWidth.toFixed(
          1
        )}mm, current width would be ${shelf.filledWidth.toFixed(1)}mm`
      );
    }
  }

  validateProductPlacement(
    layout: PlanogramLayout,
    dropIndex: number,
    shelfIndex: number,
    product: PlanogramProduct,
    productReference: Map<number, ProductReference>,
    shelfMaxWidth: number
  ): void {
    const drop = layout.drops[dropIndex];
    const shelf = drop.shelves[shelfIndex];
    const productRef = productReference.get(product.productId);

    if (!productRef) {
      throw new ValidationError('Product reference not found');
    }

    const productWidth = this.layoutUtils.calculateProductWidth(
      productRef,
      product
    );
    const productHeight = this.layoutUtils.calculateProductHeight(
      productRef,
      product
    );

    // Calculate new shelf width
    const updatedShelf = {
      ...shelf,
      filledWidth:
        this.layoutUtils.calculateShelfFilledWidth(layout, shelf) +
        productWidth,
    };

    // Check shelf width using existing method
    try {
      this.validateShelfWidth(updatedShelf, shelfMaxWidth);
    } catch (error) {
      if (error instanceof ValidationError) {
        throw new ValidationError(
          `Adding this product would exceed shelf width: ${error.message}`
        );
      }
      throw error;
    }

    // Calculate new shelf height
    const newShelfHeight = Math.max(shelf.shelfHeight, productHeight);

    // Create temporary drop copy to validate total height
    const dropCopy = { ...drop };
    dropCopy.shelves = [...drop.shelves];
    dropCopy.shelves[shelfIndex] = {
      ...shelf,
      shelfHeight: newShelfHeight,
    };

    try {
      this.validateDropHeight(dropCopy, layout);
    } catch (error) {
      if (error instanceof ValidationError) {
        throw new ValidationError(
          `Adding this product would make the drop too tall: ${error.message}`
        );
      }
      throw error;
    }
  }

  validateAddProductToStack(
    layout: PlanogramLayout,
    dropIndex: number,
    shelfIndex: number,
    stackIndex: number,
    product: PlanogramProduct,
    productReference: Map<number, ProductReference>
  ): void {
    const drop = layout.drops[dropIndex];
    const shelf = drop.shelves[shelfIndex];
    const stack = shelf.stacks[stackIndex];
    const productRef = productReference.get(product.productId);
    if (!productRef) throw new ValidationError('Product reference not found');

    // --- Calculate height change ---
    const productHeight = this.layoutUtils.calculateProductHeight(
      productRef,
      product
    );
    const currentStackHeight = this.layoutUtils.calculateStackHeight(
      stack,
      layout
    );
    const newStackHeight = currentStackHeight + productHeight;
    const newShelfHeight = Math.max(shelf.shelfHeight, newStackHeight);

    // --- Check for possible width overflow ---
    const existingStackWidth = this.layoutUtils.calculateStackWidth(stack, layout);
    const productWidth = this.layoutUtils.calculateProductWidth(
      productRef,
      product
    );

    // If the product width is greater than current stack width, shelf width may increase
    const addedWidth = Math.max(productWidth - existingStackWidth, 0);
    const updatedShelf = {
      ...shelf,
      filledWidth:
        this.layoutUtils.calculateShelfFilledWidth(layout, shelf) + addedWidth,
    };

    // Ensure the shelf doesn’t exceed its maximum allowed width
    try {
      this.validateShelfWidth(
        updatedShelf,
        this.planogramService.getShelfWidth()
      );
    } catch (error) {
      if (error instanceof ValidationError) {
        throw new ValidationError(
          `Adding this product would exceed shelf width: ${error.message}`
        );
      }
      throw error;
    }

    // --- Simulate drop and validate total height ---
    const dropCopy: PlanogramDrop = { ...drop, shelves: [...drop.shelves] };
    dropCopy.shelves[shelfIndex] = { ...shelf, shelfHeight: newShelfHeight };
    this.validateDropHeight(dropCopy, layout);
  }

  validateProductFacingIncrease(
    layout: PlanogramLayout,
    dropIndex: number,
    shelfIndex: number,
    productRef: ProductReference,
    dropWidth: number
  ): void {
    //Not yet implemented
  }

  validateProductOrientation(
    layout: PlanogramLayout,
    dropIndex: number,
    shelfIndex: number,
    productIndex: number,
    newOrientation: ProductOrientation,
    dropWidth: number
  ): void {
    //Not yet implemented
  }

  validateProductMovement(
    layout: PlanogramLayout,
    sourceDropIndex: number,
    sourceShelfIndex: number,
    sourceProductIndex: number,
    targetDropIndex: number,
    targetShelfIndex: number,
    targetProductIndex: number,
    dropWidth: number
  ): void {
    //Not yet implemented
  }

  validateShelfMovement(
    layout: PlanogramLayout,
    sourceDropIndex: number,
    sourceShelfIndex: number,
    targetDropIndex: number,
    targetShelfIndex: number
  ): void {
    //Not yet implemented
  }

  validatePasteProduct(
    layout: PlanogramLayout,
    dropIndex: number,
    shelfIndex: number,
    product: PlanogramProduct,
    productReference: Map<number, ProductReference>,
    dropWidth: number
  ): void {
    //Not yet implemented
  }

  validatePasteShelf(
    layout: PlanogramLayout,
    dropIndex: number,
    shelfIndex: number | null,
    shelf: PlanogramShelf,
    dropWidth: number,
    paste: 'above' | 'below' | 'end'
  ): void {
    //Not yet implemented
  }
}
